import { TreeInstance } from '@headless-tree/core';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useTasksByFilterDocument } from 'features/tasks';
import React, { useMemo } from 'react';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import {
  TaskCardMultiSelectContextMenu,
  useTaskTreeShortcuts,
} from 'shared/components/connected/task-card-multi-select-context-menu';
import {
  InternalTreeItem,
  useTree,
} from 'shared/components/ui/virtualized-tree-list';
import { useRegisterFullCalendarDraggableItems } from 'shared/hooks/use-register-full-calendar-draggable-items';
import { useUser } from 'shared/hooks/use-user';
import { FilterDocument } from 'shared/types/filter-document';
import { GroupByMode } from 'shared/types/sorting-mode';
import { isDefaultFilterDocument } from 'shared/utils/filters/is-default-filter-document';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { NoFilterResultsState } from './components/no-filter-results-state';
import { ContextMenuProvider, useContextMenu } from './context-menu-context';
import { defaultGroupSettings, groupByLists } from './groups';
import { TaskFilterListProps } from './types';

const TaskFilterListContent: React.FC<TaskFilterListProps> = ({
  filterDocument,
  onReorder,
  customOrder,
  onSectionCollapse,
  scrollElement,
  includeDuration,
  onReset,
  denseFormat,
  includeTaskWithAction,
  groupSettings,
  defaultFilterDocument,
  excludeHiddenTodoHabits,
  defaultNewTaskDate,
  includeArchivedGoals,
  goals: goalsProp,
  canReorderGoals,
}) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const sortedGoals = useSortedGoals();
  const goals = goalsProp ?? sortedGoals;
  const tags = useSortedTags();

  // get tasks by filterDocument
  const tasks = useTasksByFilterDocument(filterDocument, {
    includeTaskWithAction,
    excludeHiddenTodoHabits,
    includeArchivedGoals,
  });

  const collapsedSections = useMemo(
    () => filterDocument.collapsedSections ?? [],
    [filterDocument.collapsedSections],
  );

  useRegisterFullCalendarDraggableItems({
    listRef: scrollElement,
    tasks,
    weekStartsOn,
    includeDuration,
    includeDueDateAsOriginDate: true,
  });

  /*
   * If there are no tasks, we typically force "No Grouping" mode to show the "New Task" row.
   * However, if we are in "Section" mode and have sections, we should stay in "Section" mode
   * to show those empty sections.
   * Same applies for "Goal" mode.
   */
  const sections = groupSettings?.[GroupByMode.Section]?.sections ?? [];

  const hasRequestedStructure =
    (filterDocument.groupByMode === GroupByMode.Section && sections.length) ||
    (filterDocument.groupByMode === GroupByMode.Goal && goals.length);

  const shouldForceNoGrouping =
    !tasks.length &&
    !hasRequestedStructure &&
    filterDocument.groupByMode !== GroupByMode.Section;

  const groupMode = shouldForceNoGrouping
    ? GroupByMode.None
    : filterDocument.groupByMode ?? GroupByMode.None;

  const GroupedList = groupByLists[groupMode] ?? groupByLists[GroupByMode.None];
  const groupSetting =
    groupSettings?.[groupMode] ??
    defaultGroupSettings[groupMode] ??
    groupSettings?.[GroupByMode.None] ??
    defaultGroupSettings[GroupByMode.None];

  const [tree] = useTree();
  const { open, position, payload, close } = useContextMenu();

  useTaskTreeShortcuts({
    tree: tree as TreeInstance<InternalTreeItem>,
    tasks,
    open,
    isContextMenuOpen: !!position,
  });

  /*
   * We only want to show the empty state if the user has active filters.
   * If there are no tasks and no filters, we show the list (which will
   * render the "New Task" row).
   *
   * Additionally, we check for `onReset`. If a parent component does NOT
   * provide `onReset` (like GoalDetail), it implies the filters are
   * contextual (base filters) and not user-removable. In that case, we
   * should NOT show the "No results" empty state, but rather the standard
   * empty list (with "New Task" row).
   */
  const hasActiveFilters = useMemo(() => {
    if (defaultFilterDocument) {
      return !isDefaultFilterDocument(
        filterDocument as FilterDocument,
        defaultFilterDocument as FilterDocument,
      );
    }

    return (
      !!filterDocument.filters &&
      Object.values(filterDocument.filters).some((f) => f !== null)
    );
  }, [filterDocument, defaultFilterDocument]);

  // If we have sections and are in section mode, we consider that a valid result state
  const isSectionGroupingWithSections =
    groupMode === GroupByMode.Section && sections.length;

  if (
    !tasks.length &&
    hasActiveFilters &&
    onReset &&
    !isSectionGroupingWithSections
  ) {
    return <NoFilterResultsState onReset={onReset} />;
  }

  return (
    <>
      <GroupedList
        groupSettings={groupSetting}
        tasks={tasks}
        lifeAreas={lifeAreas}
        goals={goals}
        tags={tags}
        sortingMode={filterDocument.sortingMode}
        onReorder={onReorder}
        customOrder={customOrder}
        collapsedSections={collapsedSections}
        onSectionCollapse={onSectionCollapse}
        weekStartsOn={weekStartsOn}
        scrollElement={scrollElement}
        denseFormat={denseFormat}
        filterDocument={filterDocument}
        isContextMenuOpen={!!position}
        defaultNewTaskDate={defaultNewTaskDate}
        canReorderGoals={canReorderGoals}
      />

      {position &&
        (payload?.tasks?.length === 1 ? (
          <TaskCardContextMenu
            location={position}
            onClose={close}
            task={payload.tasks[0]}
            initialActiveSubMenu={payload.initialActiveSubMenu}
          />
        ) : (
          <TaskCardMultiSelectContextMenu
            location={position}
            onClose={close}
            tasks={payload?.tasks ?? []}
            initialActiveSubMenu={payload?.initialActiveSubMenu}
          />
        ))}
    </>
  );
};

export const TaskFilterList: React.FC<TaskFilterListProps> = (props) => (
  <ContextMenuProvider>
    <TaskFilterListContent {...props} />
  </ContextMenuProvider>
);
